import {
  Box,
  Table,
  Text,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  BoxProps,
  Icon,
} from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';
import { RiPencilLine } from 'react-icons/ri';
import { LinkButton } from '../../../../../../../components/LinkButton';
import { Pagination } from '../../../../../../../components/Pagination';
import { TableFilters } from '../../../../../../../components/TableFilters';
import { RecipientType } from '../../../../../../../models/recipient';
import { IRecipientListItem } from '../../../../../../../services/Recipients/ListRecipientsService';

export interface IRecipient extends IRecipientListItem {
  formattedDate: string;
  formattedCommission: number | null;
}

interface IOnFilterProps {
  filter: string;
  value: string;
}

interface IRecipientTableProps extends BoxProps {
  recipients: IRecipient[];
  onFilter?: (data: IOnFilterProps) => void;
  defaultFilterBy?: string;
  defaultSortBy?: string;
  onSort?: (
    sort: 'name' | 'email' | 'commission' | 'type' | 'createdAt',
  ) => void;
  onOrder?: (order: 'ASC' | 'DESC') => void;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  totalPages?: number;
}

export const RecipientTable = ({
  recipients,
  onFilter,
  defaultFilterBy,
  defaultSortBy,
  onSort,
  onOrder,
  currentPage,
  onPageChange,
  totalPages,
  ...rest
}: IRecipientTableProps): JSX.Element => {
  const handleSort = useCallback(
    (sort: string) => {
      if (onSort) {
        onSort(sort as 'name' | 'email' | 'commission' | 'type' | 'createdAt');
      }
    },
    [onSort],
  );

  const filterOptions = useMemo(
    () => [
      {
        label: 'Nome',
        value: 'name',
      },
      {
        label: 'App',
        value: 'appName',
      },
      {
        label: 'E-mail',
        value: 'email',
      },
    ],
    [],
  );

  const orderingOptions = useMemo(
    () => [
      {
        label: 'Data de cadastro',
        value: 'createdAt',
      },
      {
        label: 'Nome',
        value: 'name',
      },
      {
        label: 'E-mail',
        value: 'email',
      },
      {
        label: 'Comissão',
        value: 'commission',
      },
      {
        label: 'Tipo',
        value: 'type',
      },
    ],
    [],
  );

  return (
    <Box {...rest}>
      <TableFilters
        filterOptions={onFilter ? filterOptions : undefined}
        orderingOptions={orderingOptions}
        onFilter={onFilter}
        defaultFilterBy={defaultFilterBy}
        defaultSortBy={defaultSortBy}
        onSort={handleSort}
        onOrder={onOrder}
      />

      <Table colorScheme="blue" mt="4">
        <Thead>
          <Tr>
            <Th>Recebedor</Th>
            <Th>E-mail</Th>
            <Th>Comissão</Th>
            <Th>Tipo</Th>
            <Th>Data de cadastro</Th>
            <Th w="8" />
          </Tr>
        </Thead>

        <Tbody>
          {recipients.map((recipient) => (
            <Tr key={recipient.paymentProviderRecipientId}>
              <Td>
                <Box>
                  <Text fontWeight="bold">{recipient.name}</Text>
                  <Text fontSize="sm" color="gray.500">
                    {`App: ${recipient.app.name}`}
                  </Text>
                </Box>
              </Td>

              <Td>{recipient.email}</Td>

              <Td>
                {recipient.formattedCommission
                  ? `${recipient.formattedCommission} %`
                  : 'N/A'}
              </Td>

              <Td>{RecipientType[recipient.type]}</Td>

              <Td>{recipient.formattedDate}</Td>

              <Td>
                <LinkButton
                  icon={<Icon as={RiPencilLine} fontSize="16" />}
                  to={{
                    pathname: '/recipients/details',
                    state: {
                      recipientId: recipient.paymentProviderRecipientId,
                    },
                  }}
                >
                  Detalhes
                </LinkButton>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {!!currentPage && !!onPageChange && !!totalPages && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      )}
    </Box>
  );
};
